import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';
import { useEffect, useRef } from 'react';

import { Logger } from '@eluve/logger';

import { setCachedProxyFlag } from './getAnalyticsProviderConfig';

const FLAG_RETRIEVAL_TIMEOUT_MS = 5000;

export const useFlagRetrievalMetrics = (apiHost: string, logger: Logger) => {
  const registeredRef = useRef(false);
  const reportedRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (registeredRef.current) return;
    registeredRef.current = true;
    startTimeRef.current = Date.now();

    const reportMetrics = (
      status: 'success' | 'failure' | 'timeout',
      flagCount = 0,
    ) => {
      if (reportedRef.current) return;
      reportedRef.current = true;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const latencyMs = startTimeRef.current
        ? Date.now() - startTimeRef.current
        : 0;

      const metrics = {
        apiHost,
        status,
        flagCount,
        distinctId: posthog.get_distinct_id?.() ?? 'unknown',
        latencyMs,
      };

      logger.info('feature flag retrieval metric', metrics);

      if (status === 'failure') {
        Sentry.captureException(new Error(`feature flag retrieval ${status}`), {
          extra: metrics,
        });
      } else if (status === 'timeout') {
        logger.warn('feature flag retrieval timed out', metrics);
      }
    };

    timeoutRef.current = setTimeout(() => {
      reportMetrics('timeout');
    }, FLAG_RETRIEVAL_TIMEOUT_MS);

    const unsubscribe = posthog.onFeatureFlags((flags, _variants, context) => {
      // Skip callbacks without errorsLoading being a boolean type
      if (typeof context?.errorsLoading !== 'boolean') {
        return;
      }

      const proxyEnabled =
        posthog.isFeatureEnabled('USE_POSTHOG_PROXY') ?? false;
      setCachedProxyFlag(proxyEnabled);

      const status = context.errorsLoading ? 'failure' : 'success';
      reportMetrics(status, flags.length);
    });

    return () => {
      registeredRef.current = false;
      unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [apiHost, logger]);
};
